/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { useLocation } from "react-router-dom";
import { getPortalAdapter } from "@/api/portal-adapter";
import { trackingEnabled } from "@/config/runtime";
import { getEngagementTracker, getRouteContext, type RouteContext } from "@/lib/tracker";

interface TrackerContextValue {
  pageType: RouteContext["pageType"] | null;
  routeContext: RouteContext | null;
  track: (event: string, properties: Record<string, string | number | boolean | null>) => void;
}

interface TrackingProviderProps {
  children: React.ReactNode;
  suppressPageView?: boolean;
}

const noopContext: TrackerContextValue = {
  pageType: null,
  routeContext: null,
  track: () => undefined,
};

const TrackerContext = React.createContext<TrackerContextValue>(noopContext);

export function TrackingProvider({ children, suppressPageView = false }: TrackingProviderProps) {
  const adapter = getPortalAdapter();
  const location = useLocation();
  const tracker = React.useMemo(() => getEngagementTracker(), []);
  const routeContext = React.useMemo(() => getRouteContext(location.pathname), [location.pathname]);

  React.useEffect(() => {
    tracker.configure({
      enabled: trackingEnabled(),
      postEvents: adapter.postEvents,
    });
  }, [adapter.postEvents, tracker]);

  React.useEffect(() => {
    tracker.onRouteChange(location.pathname, routeContext, suppressPageView);
  }, [location.pathname, routeContext, suppressPageView, tracker]);

  const track = React.useCallback(
    (event: string, properties: Record<string, string | number | boolean | null>) => {
      if (!routeContext) return;
      tracker.track({ event, properties, routeContext });
    },
    [routeContext, tracker],
  );

  const value = React.useMemo<TrackerContextValue>(() => ({
    pageType: routeContext?.pageType ?? null,
    routeContext,
    track,
  }), [routeContext, track]);

  return <TrackerContext.Provider value={value}>{children}</TrackerContext.Provider>;
}

export function useTracker(): TrackerContextValue {
  return React.useContext(TrackerContext);
}
